import React, { useState, useEffect, useRef } from "react";
import Box from "@material-ui/core/Box";
import makeStyles from "@material-ui/core/styles/makeStyles";
import BackIcon from "@material-ui/icons/ArrowBack";
import {
  IconButton,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Typography
} from "@material-ui/core";
import API from "../../../utils/axios";
import BlackButton from "../BlackButton";
import { useSnackbar } from "notistack";
import { CopyToClipboard } from "react-copy-to-clipboard";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import { remCalc } from "../../../utils/helpers";
import { httpRegex, urlRegex } from "../../../utils/regExps";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import Form from "../../common/Form";

const useStyles = makeStyles(theme => ({
  header: {
    height: 80,
    boxSizing: "border-box",
    borderBottom: theme.variables.border
  },
  formControl: {
    "& .MuiIconButton-colorPrimary.Mui-checked svg": {
      color: theme.palette.primary.main
    },
    "& .MuiCheckbox-root": {
      margin: theme.spacing(-0.5, 0.375, -0.5, 0),
      padding: theme.spacing(1)
    },
    "& label + label": {
      marginTop: theme.spacing(1)
    }
  },
  title: {
    fontWeight: 500,
    fontSize: "1.25rem",
    lineHeight: "1.4375rem"
  },
  inputsTitle: {
    lineHeight: remCalc(19),
    marginBottom: theme.spacing(2),
    fontWeight: 500
  },
  inputWrapper: {
    boxSizing: "border-box"
  },
  eventsHeader: {
    lineHeight: "1.1875",
    color: "#202124",
    fontWeight: 500,
    margin: theme.spacing(3.5, 0, 2)
  },
  label: {
    fontSize: "0.9375rem"
  }
}));

const webhooksObj = {
  projectPublished: "project_publish",
  stopCompleted: "stop_delivered",
  stopSkipped: "stop_skipped",
  routeStart: "route_start"
};

function AddWebhook({
  currentWebhook,
  setCurrentWebhook,
  setLoading,
  eventTypes,
  setWebhooks,
  webhooks,
  handleClose,
  setAddWebhookVisible
}) {
  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();

  const initial_checked = useRef(
    currentWebhook ? currentWebhook.event_type : []
  );

  const [saveDisabled, setSaveDisabled] = useState(false);

  const [checkboxValues, setCheckboxValues] = useState({
    projectPublished: currentWebhook
      ? currentWebhook.event_type
          .map(event_type => event_type.name)
          .includes(webhooksObj.projectPublished)
      : false,
    stopCompleted: currentWebhook
      ? currentWebhook.event_type
          .map(event_type => event_type.name)
          .includes(webhooksObj.stopCompleted)
      : false,
    stopSkipped: currentWebhook
      ? currentWebhook.event_type
          .map(event_type => event_type.name)
          .includes(webhooksObj.stopSkipped)
      : false,
    routeStart: currentWebhook
      ? currentWebhook.event_type
          .map(event_type => event_type.name)
          .includes(webhooksObj.routeStart)
      : false
  });

  const [url, setUrl] = useState(
    currentWebhook ? currentWebhook.url : "https://"
  );
  const [secret, setWebhookSecret] = useState(
    currentWebhook ? currentWebhook.secret : ""
  );
  const [urlError, setUrlError] = useState("");

  // Format url on paste event
  const setModifiedUrl = newUrl => {
    if (newUrl.length !== url.length + 1 && newUrl.slice(0, 8) === "https://") {
      const pasted = newUrl.slice(8, 16);
      if (httpRegex.test(pasted)) {
        setUrl(newUrl.slice(8));
      }
    }
    setUrl(newUrl);
  };

  useEffect(() => {
    const checkedValues = Object.keys(checkboxValues).filter(
      key => checkboxValues[key]
    );

    const equalValues = initial_checked.current.every(el =>
      checkedValues.some(item => el.name.includes(webhooksObj[item]))
    );

    if (
      checkedValues.length &&
      ((!equalValues &&
        checkedValues.length === initial_checked.current.length) ||
        checkedValues.length !== initial_checked.current.length ||
        (currentWebhook && currentWebhook.url !== url && url !== "https://"))
    ) {
      setSaveDisabled(false);
    } else {
      setSaveDisabled(true);
    }
  }, [checkboxValues, currentWebhook, url]);

  useEffect(() => {
    if (currentWebhook) {
      setUrl(currentWebhook.url);
      setWebhookSecret(currentWebhook.secret);
    }
  }, [currentWebhook]);

  const handleChange = name => event => {
    setCheckboxValues({ ...checkboxValues, [name]: event.target.checked });
  };

  const { projectPublished, stopCompleted, stopSkipped, routeStart } =
    checkboxValues;

  const handleUpdate = () => {
    if (!urlRegex.test(url.trim())) {
      if (!httpRegex.test(url.trim())) {
        setUrlError("Invalid URL: must begin with https://");
      } else {
        setUrlError("Please enter a valid url");
      }
      return;
    }

    const body = { url: url.trim() };

    const event_type = Object.keys(checkboxValues)
      .filter(key => checkboxValues[key])
      .map(key => {
        return eventTypes.find(eventType => eventType.name === webhooksObj[key])
          .id;
      });

    body.event_type = event_type;

    setLoading(true);
    if (currentWebhook) {
      // Update webhook
      API.put(`v1/webhook/${currentWebhook.id}`, body)
        .then(res => {
          setWebhooks(
            webhooks.map(webhook =>
              webhook.id === currentWebhook.id ? res.data : webhook
            )
          );
          enqueueSnackbar("Webhook successfully updated");
          setAddWebhookVisible(false);
          setCurrentWebhook(null);
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          setUrlError("");
          setLoading(false);
        });
    } else {
      // Add webhook
      API.post("v1/webhook", body)
        .then(res => {
          setCurrentWebhook(res.data);
          setWebhooks([...webhooks, res.data]);
          enqueueSnackbar("Webhook successfully added");
          setAddWebhookVisible(false);
          setCurrentWebhook(null);
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          setUrlError("");
          setLoading(false);
        });
    }
  };

  // Copy success
  const onCopySuccess = () => {
    enqueueSnackbar("Copied to clipboard");
  };

  const resetSecret = id => {
    setLoading(true);
    API.patch(`v1/webhook/secret/${id}`)
      .then(res => {
        setWebhooks(
          webhooks.map(webhook =>
            webhook.id === id ? { ...webhook, secret: res.data } : webhook
          )
        );
        setWebhookSecret(res.data);
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {/* Header */}
      <Box
        className={classes.header}
        display="flex"
        alignItems="center"
        pl={2}
        pr={3}
      >
        <Box className={classes.backButton} mr={2}>
          <IconButton onClick={() => handleClose()} data-cy="backToWebhooks">
            <BackIcon />
          </IconButton>
        </Box>
        <Typography variant="h5" className={classes.title}>
          {currentWebhook ? "Edit webhook endpoint" : "Add new endpoint"}
        </Typography>
      </Box>

      {/* Body */}
      <Box
        pt={4}
        pl={3}
        pr={5}
        display="flex"
        flexDirection="column"
        height="100%"
        style={{ boxSizing: "border-box" }}
      >
        <Box>
          <Box
            className={classes.inputWrapper}
            display="flex"
            justifyContent="space-between"
            flexWrap={"wrap"}
          >
            <Typography variant={"body1"} className={classes.inputsTitle}>
              Endpoint
            </Typography>
            <Form
              data={[
                [
                  {
                    renderInputField: true,
                    label: "Endpoint URL",
                    name: "endpointUrl",
                    type: "text",
                    dataCy: "endpointUrl",
                    autoFocus: true,
                    placeholder: "https://",
                    shrink: true,
                    value: url,
                    error: !!urlError,
                    helperText: urlError ? urlError : " ",
                    onChange: e => setModifiedUrl(e.target.value),
                    onPatse: e => {
                      const text = e.clipboardData.getData("Text");
                      if (text && text.length) {
                        if (url === "https://" && httpRegex.test(text)) {
                          e.preventDefault();
                          setUrl(text);
                        }
                      }
                    }
                  }
                ],
                [
                  {
                    renderInputField: currentWebhook,
                    label: "Signing secret",
                    name: "signingSecret",
                    dataCy: "signingSecret",
                    value: secret,
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <OptimizedTooltip title={"Copy to clipboard"}>
                          <CopyToClipboard
                            text={secret}
                            onCopy={onCopySuccess}
                            className={classes.iconButton}
                          >
                            <IconButton aria-label="copy signing secret">
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </CopyToClipboard>
                        </OptimizedTooltip>
                        <OptimizedTooltip title={"Reset signing secret"}>
                          <IconButton
                            aria-label="reset signing secret"
                            className={classes.iconButton}
                            onClick={() => resetSecret(currentWebhook.id)}
                          >
                            <AutorenewIcon fontSize="small" />
                          </IconButton>
                        </OptimizedTooltip>
                      </InputAdornment>
                    )
                  }
                ]
              ]}
            />
          </Box>

          <Box>
            <Typography className={classes.eventsHeader}>
              Select events
            </Typography>
            <FormControl component="fieldset" className={classes.formControl}>
              <FormGroup>
                <FormControlLabel
                  classes={{ label: classes.label }}
                  control={
                    <Checkbox
                      checked={projectPublished}
                      color={"primary"}
                      onChange={handleChange("projectPublished")}
                      value="project.published"
                    />
                  }
                  label="project.published"
                />
                <FormControlLabel
                  classes={{ label: classes.label }}
                  control={
                    <Checkbox
                      checked={stopCompleted}
                      color={"primary"}
                      onChange={handleChange("stopCompleted")}
                      value="stop.completed"
                    />
                  }
                  label="stop.completed"
                />
                <FormControlLabel
                  classes={{ label: classes.label }}
                  control={
                    <Checkbox
                      checked={stopSkipped}
                      color={"primary"}
                      onChange={handleChange("stopSkipped")}
                      value="stop.skipped"
                    />
                  }
                  label="stop.skipped"
                />
                <FormControlLabel
                  classes={{ label: classes.label }}
                  control={
                    <Checkbox
                      checked={routeStart}
                      color={"primary"}
                      onChange={handleChange("routeStart")}
                      value="route.start"
                    />
                  }
                  label="route.start"
                />
              </FormGroup>
            </FormControl>
          </Box>
        </Box>
        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" py={1} mt={3}>
          <BlackButton
            content="Save"
            handleClick={handleUpdate}
            disabled={saveDisabled}
            data-cy="save"
          />
        </Box>
      </Box>
    </>
  );
}

export default AddWebhook;
